/**
 * Project Kanban (`/kanban`).
 *
 * The primary dashboard surface per UX §6 — gives operators an at-a-glance
 * view of every active project bucketed by lifecycle state. Cards float to
 * the top of their column as they're updated; overdue ones wear a chip.
 *
 * Filters: search + client + overdueOnly + includeClosed. URL-driven so
 * filters survive reload + back button.
 *
 * Click a card → /projects/:id (the detail page).
 *
 * No drag-and-drop. Transitions happen via explicit actions on the detail
 * page (approve / reject / close); the Kanban is read-only.
 */

import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { AlertTriangle, ClipboardList, Search, X } from 'lucide-react';
import { useState, useMemo } from 'react';
import { formatRelative } from '@procurement/shared';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorPanel } from '@/components/ui/error-panel';
import { Input } from '@/components/ui/form-field';
import { PageHeader } from '@/components/ui/page-header';
import { ProjectNumber } from '@/components/ui/project-number';
import { ProjectProgressBar } from '@/components/project-progress-bar';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/cn';
import { usePageTitle } from '@/lib/use-page-title';
import {
  applyOverdueFilter,
  buildKanbanBoard,
  type KanbanColumn,
} from '@/lib/kanban';
import {
  useProjectsList,
  type VisibilityRow,
} from '@/lib/visibility-queries';

interface KanbanSearch {
  q?: string;
  overdueOnly?: boolean;
  includeClosed?: boolean;
}

export function KanbanPage() {
  usePageTitle('Project board');
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as KanbanSearch;
  const [searchInput, setSearchInput] = useState(search.q ?? '');

  // Fetch a large page so the Kanban shows everything client-side. With small
  // project counts (≤ a few hundred) this is cheaper than per-column queries
  // and keeps re-bucketing snappy.
  const list = useProjectsList({
    pageSize: 500,
    search: search.q,
    excludeClosed: !search.includeClosed,
  });

  function updateSearch(next: Partial<KanbanSearch>) {
    void navigate({
      to: '/kanban',
      search: (prev: KanbanSearch) => {
        const merged: KanbanSearch = { ...prev, ...next };
        // Drop empty values so the URL stays clean
        if (!merged.q) delete merged.q;
        if (!merged.overdueOnly) delete merged.overdueOnly;
        if (!merged.includeClosed) delete merged.includeClosed;
        return merged;
      },
      // Patch 5+ — keep filter toggles out of the back stack.
      replace: true,
    });
  }

  function submitSearch(e: React.FormEvent) {
    e.preventDefault();
    updateSearch({ q: searchInput.trim() || undefined });
  }

  const board = useMemo(() => {
    const rows = list.data?.items ?? [];
    const filtered = applyOverdueFilter(rows, Boolean(search.overdueOnly));
    return buildKanbanBoard(filtered, { includeClosed: Boolean(search.includeClosed) });
  }, [list.data?.items, search.overdueOnly, search.includeClosed]);

  return (
    <>
      <PageHeader
        title="Project board"
        subtitle="Every project, bucketed by lifecycle. Click a card to drill in."
      />

      {/* Filter bar */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <form onSubmit={submitSearch} className="flex items-center gap-2">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-tertiary"
              strokeWidth={1.5}
            />
            <Input
              placeholder="Search project number, title, client"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="h-8 w-72 pl-7 text-xs"
            />
          </div>
        </form>

        <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-secondary">
          <input
            type="checkbox"
            checked={Boolean(search.overdueOnly)}
            onChange={(e) => updateSearch({ overdueOnly: e.target.checked || undefined })}
            className="h-3.5 w-3.5 rounded border-border-default"
          />
          Overdue only
        </label>

        <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-secondary">
          <input
            type="checkbox"
            checked={Boolean(search.includeClosed)}
            onChange={(e) => updateSearch({ includeClosed: e.target.checked || undefined })}
            className="h-3.5 w-3.5 rounded border-border-default"
          />
          Show closed
        </label>

        {(search.q || search.overdueOnly || search.includeClosed) ? (
          <button
            type="button"
            onClick={() => {
              setSearchInput('');
              void navigate({ to: '/kanban', search: {} as KanbanSearch });
            }}
            className="inline-flex items-center gap-1 rounded-sm border border-border-default bg-surface px-2 py-1 text-xs text-tertiary hover:border-border-strong"
          >
            <X className="h-3 w-3" strokeWidth={1.5} />
            Clear filters
          </button>
        ) : null}

        <div className="ml-auto text-xs text-secondary">
          {board.totalProjects} project{board.totalProjects === 1 ? '' : 's'}
          {board.totalOverdue > 0 ? (
            <span className="ml-2 text-warning-strong">· {board.totalOverdue} overdue</span>
          ) : null}
        </div>
      </div>

      {/* Board */}
      {list.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner className="h-6 w-6 text-secondary" />
        </div>
      ) : list.isError || !list.data ? (
        // Patch 5 — explicit error state so a 500 doesn't read as "no projects".
        <Card>
          <ErrorPanel
            title="Could not load projects"
            onRetry={() => list.refetch()}
          />
        </Card>
      ) : board.totalProjects === 0 ? (
        <Card>
          <EmptyState
            icon={<ClipboardList className="h-6 w-6" strokeWidth={1.5} />}
            title="No projects match your filters"
            body={
              search.q
                ? `Nothing found for "${search.q}".`
                : 'Once projects are added they will appear here, grouped by lifecycle.'
            }
          />
        </Card>
      ) : (
        /*
         * Patch P19 — board uses fixed-width columns + horizontal scroll
         * instead of a responsive grid. Standard kanban UX (Trello,
         * Linear, GitHub Projects). On a 1366px monitor the prior grid
         * showed 4 columns with significant horizontal whitespace and
         * never showed CLOSED unless the viewport was ≥1280px. Now every
         * column is ~280px and the board scrolls horizontally if total
         * width exceeds the viewport — predictable on any screen.
         */
        <div className="-mx-8 overflow-x-auto px-8">
          <div className="flex gap-3" style={{ minWidth: 'max-content' }}>
            {board.columns.map((col) => (
              <div key={col.id} className="w-[280px] shrink-0">
                <Column column={col} />
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Column
// ---------------------------------------------------------------------------

function Column({ column }: { column: KanbanColumn }) {
  return (
    <div className="flex min-h-[200px] flex-col rounded-md border border-border-default bg-base/40">
      <div className="flex items-center justify-between border-b border-border-default px-3 py-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-secondary">
          {column.label}
        </span>
        <span className="text-xs text-secondary tabular-nums">
          {column.count}
          {column.overdueCount > 0 ? (
            <span className="ml-1 text-warning-strong">
              ({column.overdueCount} overdue)
            </span>
          ) : null}
        </span>
      </div>
      <div className="flex-1 space-y-2 p-2">
        {column.projects.length === 0 ? (
          <div className="px-1 py-4 text-center text-xs text-tertiary/60">No projects</div>
        ) : (
          column.projects.map((p) => <KanbanCard key={p.projectId} project={p} />)
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

function KanbanCard({ project }: { project: VisibilityRow }) {
  return (
    <Link
      to="/projects/$projectId"
      params={{ projectId: project.projectId }}
      className={cn(
        'group block rounded-sm border bg-surface px-2.5 py-2 transition-colors',
        project.isOverdue
          ? 'border-warning/40 hover:border-warning'
          : 'border-border-default hover:border-accent',
      )}
    >
      <div className="flex items-baseline justify-between gap-2">
        <ProjectNumber number={project.projectNumber} size="sm" />
        {project.isOverdue ? (
          <AlertTriangle
            className="h-3 w-3 shrink-0 text-warning-strong"
            strokeWidth={1.5}
            aria-label="Overdue"
          />
        ) : null}
      </div>
      <div className="mt-0.5 truncate text-sm font-medium text-primary group-hover:text-accent-strong">
        {project.projectTitle}
      </div>
      <div className="mt-0.5 truncate text-xs text-secondary">{project.clientName}</div>

      {/* E20: progress signal — single-glance answer to "how far along is
       * this project?". A project at the bottom of "Active" with everything
       * already through inspection visually announces itself as different
       * from one that just started. */}
      <div className="mt-1.5">
        <ProjectProgressBar buckets={project.partsProgress} variant="compact" />
      </div>

      {/* Status row: parts, batches, last-updated */}
      <div className="mt-1.5 flex items-center gap-2 text-xs text-secondary">
        {project.partCount > 0 ? (
          <span className="tabular-nums">
            {project.partCount} part{project.partCount === 1 ? '' : 's'}
          </span>
        ) : null}
        {project.activeBatchCount > 0 ? (
          <span className="tabular-nums">
            · {project.activeBatchCount} batch{project.activeBatchCount === 1 ? '' : 'es'}
          </span>
        ) : null}
        {project.unresolvedInspectionCount > 0 ? (
          <span className="text-warning-strong">
            · {project.unresolvedInspectionCount} unresolved
          </span>
        ) : null}
      </div>

      {/* Footer */}
      <div className="mt-1 text-[10px] text-secondary">
        Updated {formatRelative(project.lastUpdatedAt)}
      </div>
    </Link>
  );
}
